import {
  calculateWeightedAverages,
  transformFragmentToCostChange,
} from '@sales-portal/domain/pricing/screens/management/blocs/transformers/use-pricing-management-transformers';
import { useCallback, useEffect, useRef } from 'react';
import { useGeneratedPricingVariantPackCostChangeDataLazyQuery } from '@sales-portal/domain/pricing/screens/management/blocs/documents/variant-pack-cost-change-data.generated';
import { useGeneratedSearchVariantPacksForBusinessOrganizationLazyQuery } from '@sales-portal/domain/pricing/screens/management/blocs/documents/search-variant-packs-for-business-organization.generated';
import { usePricingCatalogSearchStore } from '@sales-portal/domain/pricing/screens/management/blocs/stores/pricing-catalog-search-store';

interface UsePricingCatalogSearchLoaderParams {
  /** Invoked when a catalog search completes successfully (results or empty). */
  onComplete?: () => void;
  /** Invoked when a catalog search fails. */
  onError?: () => void;
}

/**
 * Executes the cost-changes catalog search as a two-step chained fetch:
 *
 * 1. `SearchVariantPacksForBusinessOrganization` → matching variant pack UUIDs.
 * 2. `PricingVariantPackCostChangeData` scoped to those UUIDs → cost-change rows,
 *    transformed through the same pipeline as the base list so they render
 *    identically.
 *
 * Uses a `latestQueryRef` guard (checked after each await) so out-of-order
 * responses from a superseded search never overwrite newer results — Apollo
 * can't cancel an in-flight request.
 *
 * Variant packs already present in the local list (passed as
 * `excludeVariantPackUUIDs`) are dropped before the cost-change fetch, so
 * catalog results stay unique to the existing cost-change items.
 *
 * Results are server-filtered only: pending price edits are not merged and the
 * category filter is not applied to catalog rows (catalog search is a global
 * escalation, independent of the local list's filters).
 */
export const usePricingCatalogSearchLoader = ({
  onComplete,
  onError,
}: UsePricingCatalogSearchLoaderParams = {}) => {
  const onCatalogSearch = usePricingCatalogSearchStore(
    (s) => s.onCatalogSearch,
  );
  const onCatalogSearchError = usePricingCatalogSearchStore(
    (s) => s.onCatalogSearchError,
  );
  const onCatalogSearchResults = usePricingCatalogSearchStore(
    (s) => s.onCatalogSearchResults,
  );
  const latestQueryRef = useRef<string>('');

  useEffect(() => {
    return () => {
      latestQueryRef.current = '';
    };
  }, []);

  const searchVariantPacks =
    useGeneratedSearchVariantPacksForBusinessOrganizationLazyQuery({
      fetchPolicy: 'no-cache',
    });
  const getCostChangeData =
    useGeneratedPricingVariantPackCostChangeDataLazyQuery({
      fetchPolicy: 'no-cache',
    });

  const executeCatalogSearch = useCallback(
    async (searchTerm: string, excludeVariantPackUUIDs?: Set<string>) => {
      latestQueryRef.current = searchTerm;
      onCatalogSearch(searchTerm);

      try {
        const searchResult = await searchVariantPacks({
          variables: { query: searchTerm },
        });

        if (searchTerm !== latestQueryRef.current) {
          return;
        }

        const variantPackUUIDs =
          searchResult.data?.searchVariantPacksForBusinessOrganization?.reduce<
            string[]
          >((acc, match) => {
            if (!excludeVariantPackUUIDs?.has(match.variant_pack_id)) {
              acc.push(match.variant_pack_id);
            }
            return acc;
          }, []) ?? [];

        if (variantPackUUIDs.length === 0) {
          onCatalogSearchResults([]);
          onComplete?.();
          return;
        }

        const dataResult = await getCostChangeData({
          variables: { variantPackUUIDs },
        });

        if (searchTerm !== latestQueryRef.current) {
          return;
        }

        const fragments =
          dataResult.data
            ?.getVariantPackCostChangeDataForBusinessOrganization ?? [];
        const perRestaurantRows = fragments
          .map(transformFragmentToCostChange)
          .filter((row): row is NonNullable<typeof row> => row !== null);

        onCatalogSearchResults(calculateWeightedAverages(perRestaurantRows));
        onComplete?.();
      } catch (error) {
        if (searchTerm !== latestQueryRef.current) {
          return;
        }
        onCatalogSearchError(
          error instanceof Error ? error : new Error('Catalog search failed'),
        );
        onError?.();
      }
    },
    [
      getCostChangeData,
      onCatalogSearch,
      onCatalogSearchError,
      onCatalogSearchResults,
      onComplete,
      onError,
      searchVariantPacks,
    ],
  );

  // Invalidate any in-flight search so a pending response can't flash results
  // after the user exits catalog mode (e.g. via Escape/clear).
  const cancelCatalogSearch = useCallback(() => {
    latestQueryRef.current = '';
  }, []);

  return { cancelCatalogSearch, executeCatalogSearch };
};
